import { useCallback, useEffect, useState } from 'react'

/**
 * `data-theme="dark"` on <html>, persisted to localStorage. Matches the
 * mockup's inline boot script — initial value is read on mount so SSR
 * never flashes the wrong palette. Listens to prefers-color-scheme
 * changes only when no explicit user preference is stored.
 */

export const Theme = {
  Light: 'light',
  Dark: 'dark',
} as const
export type Theme = (typeof Theme)[keyof typeof Theme]

const STORAGE_KEY = 'theme'

function detectInitial(): Theme {
  if (typeof window === 'undefined') return Theme.Light
  const stored = window.localStorage.getItem(STORAGE_KEY)
  if (stored === Theme.Dark || stored === Theme.Light) return stored
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? Theme.Dark : Theme.Light
}

function applyTheme(theme: Theme): void {
  if (typeof document === 'undefined') return
  document.documentElement.setAttribute('data-theme', theme)
}

export function useTheme(): { theme: Theme; setTheme: (t: Theme) => void; toggle: () => void } {
  const [theme, setThemeState] = useState<Theme>(detectInitial)

  useEffect(() => {
    applyTheme(theme)
  }, [theme])

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next)
    try { window.localStorage.setItem(STORAGE_KEY, next) } catch { /* private mode */ }
  }, [])

  const toggle = useCallback(() => {
    setThemeState((prev) => {
      const next = prev === Theme.Dark ? Theme.Light : Theme.Dark
      try { window.localStorage.setItem(STORAGE_KEY, next) } catch { /* private mode */ }
      return next
    })
  }, [])

  return { theme, setTheme, toggle }
}
